import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

export const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer data-testid="main-footer" className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="space-y-6">
            <span className="font-heading font-bold text-2xl text-white">
              mascot<span className="text-brand-orange">&</span>candy
            </span>
            <p className="font-body text-gray-400 leading-relaxed">
              {t('footer.slogan')}
            </p>
            <div className="flex gap-4">
              <a href="https://www.facebook.com/mascotcandy" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-gray-800 hover:bg-brand-orange transition-colors" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://www.instagram.com/mascotcandy/" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-gray-800 hover:bg-brand-orange transition-colors" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.tiktok.com/@mascotcandy" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-gray-800 hover:bg-brand-orange transition-colors" aria-label="TikTok">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-6">
            <h4 className="font-heading font-bold text-lg">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              <li>
                <a href="/#services" className="font-body text-gray-400 hover:text-brand-orange transition-colors">
                  {t('nav.services')}
                </a>
              </li>
              <li>
                <a href="/#gallery" className="font-body text-gray-400 hover:text-brand-orange transition-colors">
                  {t('nav.gallery')}
                </a>
              </li>
              <li>
                <a href="/#about" className="font-body text-gray-400 hover:text-brand-orange transition-colors">
                  {t('nav.about')}
                </a>
              </li>
              <li>
                <a href="/#contact" className="font-body text-gray-400 hover:text-brand-orange transition-colors">
                  {t('nav.contact')}
                </a>
              </li>
            </ul>
          </div>

          {/* Book Services */}
          <div className="space-y-6">
            <h4 className="font-heading font-bold text-lg">{t('footer.ourServices')}</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/book/birthday" className="font-body text-gray-400 hover:text-brand-orange transition-colors">
                  {t('footer.birthdayPackage')}
                </Link>
              </li>
              <li>
                <Link to="/book/mascot" className="font-body text-gray-400 hover:text-brand-orange transition-colors">
                  {t('footer.mascotReservation')}
                </Link>
              </li>
              <li>
                <Link to="/book/candy-bar" className="font-body text-gray-400 hover:text-brand-orange transition-colors">
                  {t('footer.candyBar')}
                </Link>
              </li>
              <li>
                <Link to="/register/stem" className="font-body text-gray-400 hover:text-brand-orange transition-colors">
                  {t('footer.stemEducation')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-6" id="contact">
            <h4 className="font-heading font-bold text-lg">{t('footer.contactUs')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-brand-orange flex-shrink-0 mt-1" />
                <span className="font-body text-gray-400">
                  Yaounde, Cameroon
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-brand-orange flex-shrink-0" />
                <a href="tel:695521384" className="font-body text-gray-400 hover:text-brand-orange transition-colors">
                  695521384
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-brand-orange flex-shrink-0" />
                <a href="mailto:hello@mascotcandy.com" className="font-body text-gray-400 hover:text-brand-orange transition-colors">
                  hello@mascotcandy.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-brand-orange flex-shrink-0 mt-1" />
                <span className="font-body text-gray-400">
                  Mon - Sun: 9:00 AM - 8:00 PM
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="font-body text-gray-500 text-sm text-center md:text-left">
              {t('footer.copyright')}
            </p>
            <div className="flex gap-6">
              <a href="#" className="font-body text-gray-500 hover:text-brand-orange text-sm transition-colors">
                {t('footer.privacyPolicy')}
              </a>
              <a href="#" className="font-body text-gray-500 hover:text-brand-orange text-sm transition-colors">
                {t('footer.termsOfService')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
